import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Pager, SortTh, useLocalSort, BRANCH_LABEL } from "../../components/common";
import { ACTION_LABEL } from "../../components/PermEditor";
import { useAuth } from "../../lib/auth";

const FIELD_NAME = { "lead.phone": "Lead phone", "lead.email": "Lead email", "lead.estimated_value": "Lead value", "order.rfq_amount": "RFQ value",
  "order.po_amount": "PO value", "order.attachment_url": "PO & estimate documents", "contact.phone": "Contact phone", "contact.email": "Contact email",
  "account.revenue_potential": "Customer revenue potential" };
const LV = { edit: "text-emerald-600", view: "text-sky-600", mask: "text-amber-600", none: "text-muted-foreground" };

const Summary = ({ s }) => (
  <div className="grid gap-4 md:grid-cols-3 mb-6" data-testid="access-check-summary">
    <div className="ax-panel p-4">
      <div className="ax-label mb-1">Who</div>
      <div className="font-medium" data-testid="access-check-user-name">{s.user.name}</div>
      <div className="text-xs text-muted-foreground">{s.user.email}</div>
      <div className="text-sm mt-2"><span className="text-primary" data-testid="access-check-role">{s.user.role_name}</span> · {(s.user.branches || []).includes("*") ? "All branches" : (s.user.branches || []).map((b) => BRANCH_LABEL[b] || b).join(", ") || "no branch"}</div>
      <div className="text-xs text-muted-foreground mt-1">Sheet owner name: {s.user.staff_name || "not linked"}{s.user.team ? ` · Team ${s.user.team}` : ""}</div>
    </div>
    {Object.entries(s.modules).map(([m, v]) => (
      <div key={m} className="ax-panel p-4" data-testid={`access-check-module-${m}`}>
        <div className="ax-label mb-1">{v.label}</div>
        <div className="text-2xl font-display" data-testid={`access-check-count-${m}`}>{v.open.toLocaleString()} <span className="text-sm text-muted-foreground">of {v.total.toLocaleString()} can open</span></div>
        <div className="text-xs mt-1">Sees {v.scope_text}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{v.actions.length ? v.actions.map((a) => ACTION_LABEL[a] || a).join(", ") : "No rights"}</div>
      </div>
    ))}
  </div>
);

const Extras = ({ s }) => (
  <div className="grid gap-4 md:grid-cols-2 mb-6">
    <div className="ax-panel p-4" data-testid="access-check-fields">
      <div className="ax-label mb-2">Sensitive details</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">{Object.entries(s.fields).map(([f, l]) => (
        <div key={f} className="flex justify-between gap-2 min-w-0"><span className="break-words min-w-0">{FIELD_NAME[f] || f}</span><span className={LV[l]}>{{ edit: "can edit", view: "can see", mask: "partly hidden", none: "hidden" }[l]}</span></div>))}</div>
    </div>
    <div className="ax-panel p-4" data-testid="access-check-overrides">
      <div className="ax-label mb-2">Special access</div>
      {s.overrides.length ? s.overrides.map((o) => (
        <div key={o.entity + o.entity_id} className="text-sm flex justify-between py-0.5"><span>{o.label}</span>
          <span className={o.mode === "allow" ? "text-emerald-600" : "text-rose-600"}>{o.mode === "allow" ? "Given" : "Blocked"}</span></div>
      )) : <div className="text-sm text-muted-foreground">None. Add it from a customer or UID page.</div>}
    </div>
  </div>
);

const Records = ({ userId }) => {
  const [f, setF] = useState({ module: "sales", show: "open", q: "" });
  const [page, setPage] = useState(1);
  const [d, setD] = useState({ items: [], total: 0 });
  useEffect(() => {
    api.get(`/admin/access-check/${userId}/records`, { params: { ...f, page } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [userId, f, page]);
  const set = (k, v) => { setPage(1); setF({ ...f, [k]: v }); };
  const link = (r) => (f.module === "sales" ? `/sales/leads/${r.id}` : `/orders/uid/${r.id}`);
  const [sorted, sort] = useLocalSort(d.items, "id");
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {[["sales", "Leads"], ["orders", "UIDs"]].map(([k, l]) => (
          <button key={k} data-testid={`access-check-tab-${k}`} onClick={() => set("module", k)} className={`h-9 px-4 text-sm border rounded-full ${f.module === k ? "bg-primary text-primary-foreground" : "hover:border-primary"}`}>{l}</button>))}
        <select data-testid="access-check-show" value={f.show} onChange={(e) => set("show", e.target.value)} className="bg-muted/60 border h-9 px-2 text-sm">
          <option value="open">Can open</option><option value="closed">Can't open</option><option value="all">Everything</option></select>
        <input data-testid="access-check-search" placeholder="Search customer, UID, lead or owner… (Enter)" onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)} className="bg-muted/60 border h-9 px-2 text-sm flex-1 min-w-[220px]" />
      </div>
      <div className="ax-panel ax-scroll ax-freeze"><table className="ax-table w-full min-w-[820px]" data-testid="access-check-table">
        <thead><tr>{[["id", f.module === "sales" ? "Lead" : "UID"], ["name", "Customer"], ["branch", "Branch"], ["owner", "Owner"], ["segment", "Business"], ["can_open", "Can open?"], ["reason", "Why"]].map(([k, l]) => <SortTh key={k} k={k} label={l} sort={sort} />)}</tr></thead>
        <tbody>{sorted.map((r) => (
          <tr key={r.id} data-testid={`access-check-row-${r.id}`}>
            <td className="font-mono text-xs"><Link to={link(r)} className="hover:text-primary">{r.id}</Link></td><td className="text-sm">{r.name || "—"}</td>
            <td className="text-xs">{BRANCH_LABEL[r.branch] || r.branch}</td><td className="text-xs">{r.owner || "—"}</td><td className="text-xs">{r.segment === "repeat" ? "Repeat" : "New"}</td>
            <td>{r.can_open ? <span className="text-emerald-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Yes</span>
              : <span className="text-rose-600 text-xs inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />No</span>}</td>
            <td className="text-xs" data-testid={`access-check-reason-${r.id}`}>{r.reason}</td>
          </tr>))}
          {!d.items.length && <tr><td colSpan={7} className="text-sm text-muted-foreground py-6 text-center">Nothing here.</td></tr>}</tbody>
      </table></div>
      <Pager page={d.page || 1} total={d.total} size={50} onPage={setPage} />
    </div>
  );
};

export default function AccessCheck() {
  const { can } = useAuth();
  if (!can("admin", "manage_access")) {
    return <div className="ax-panel p-6 text-sm" data-testid="access-check-locked">Only founders can use Access check.</div>;
  }
  return <AccessCheckInner />;
}

function AccessCheckInner() {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => {
    setS(null);
    if (!uid) { return; }
    api.get(`/admin/access-check/${uid}`).then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [uid]);
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Access check" desc="Pick anyone to see exactly which leads and UIDs they can open, what they can do with them, and why." />
      <select data-testid="access-check-user-select" value={uid} onChange={(e) => setUid(e.target.value)} className="bg-muted/60 border h-10 px-3 text-sm mb-6 w-full md:w-96">
        <option value="">Choose a person…</option>
        {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} · {u.email}{u.status !== "active" ? " (inactive)" : ""}</option>)}
      </select>
      {s && <><Summary s={s} /><Extras s={s} /><Records key={uid} userId={uid} /></>}
    </div>
  );
}
